import React, { useEffect, useMemo, useState } from 'react';
import { Space, Row, Col, Avatar, Button, Popover, Input, Empty } from 'antd';
import { PlusOutlined, CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { User } from '@/common/model';

import './UserSelect.less';

interface UserSelectProps {
  userMap: Map<string, User> | Map<number, User>;
  mode: 'single' | 'multiple';
  value?: string | number | null;
  onSingleSelect: (user: User | null) => void;
  onMultipleSelect: (users: User[]) => void;
}

const matchUser = (user: User, keyword: string): boolean => {
  const text =
    `${user.userName || ''}${user.userNamePinyin || ''}${user.nickName || ''}${user.nickNamePinyin || ''}${user.phone || ''}`.toLowerCase();
  return text.includes(keyword);
};

const UserSelect: React.FC<UserSelectProps> = ({
  userMap,
  mode,
  value,
  onSingleSelect,
  onMultipleSelect,
}) => {
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (value == null || value === '') {
      setSelectedUsers([]);
      return;
    }
    const user =
      userMap.get(String(value)) ?? userMap.get(Number(value) as number);
    if (user) {
      setSelectedUsers([user]);
    }
  }, [value, userMap]);

  const allUsers = useMemo(() => Array.from(userMap.values()), [userMap]);

  const candidateUsers = useMemo(() => {
    const selectedIds = new Set(selectedUsers.map((u) => String(u.id)));
    return allUsers.filter((user) => {
      if (mode === 'multiple' && selectedIds.has(String(user.id))) {
        return false;
      }
      const keyword = search.trim().toLowerCase();
      if (!keyword) {
        return true;
      }
      return matchUser(user, keyword);
    });
  }, [allUsers, search, selectedUsers, mode]);

  const handlePick = (selectedUser: User) => {
    if (mode === 'single') {
      setSelectedUsers([selectedUser]);
      onSingleSelect(selectedUser);
    } else {
      const newSelectedUsers = [...selectedUsers, selectedUser];
      setSelectedUsers(newSelectedUsers);
      onMultipleSelect(newSelectedUsers);
    }
    setOpen(false);
    setSearch('');
  };

  const handleRemoveUser = (user: User) => {
    const updatedUsers = selectedUsers.filter(
      (u) => String(u.id) !== String(user.id),
    );
    setSelectedUsers(updatedUsers);
    if (mode === 'single') {
      onSingleSelect(null);
    } else {
      onMultipleSelect(updatedUsers);
    }
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setSearch('');
    }
  };

  const pickerPanel = (
    <div className="user-select-panel" onClick={(e) => e.stopPropagation()}>
      <Input
        allowClear
        size="small"
        prefix={<SearchOutlined />}
        placeholder="搜索姓名、拼音或手机号"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="user-select-search"
      />
      <div className="user-select-list">
        {candidateUsers.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="无匹配成员"
          />
        ) : (
          candidateUsers.map((user) => (
            <div
              key={String(user.id)}
              className="user-select-option"
              onClick={() => handlePick(user)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handlePick(user);
                }
              }}
            >
              <Avatar src={user.userAvatar} size="small" />
              <span className="user-select-option-name">{user.userName}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const showAddButton = mode === 'multiple' || selectedUsers.length === 0;

  return (
    <Space className="user-select">
      {selectedUsers.map((user) => (
        <Row key={String(user.id)} align="middle" className="selected-user">
          <Col>
            <Avatar src={user.userAvatar} size="small" />
          </Col>
          <Col flex="auto">{user.userName}</Col>
          <Col>
            <Button
              type="text"
              className="close-btn"
              icon={<CloseOutlined />}
              onClick={() => handleRemoveUser(user)}
            />
          </Col>
        </Row>
      ))}
      {showAddButton && (
        <Popover
          content={pickerPanel}
          trigger="click"
          open={open}
          onOpenChange={handleOpenChange}
          placement="bottomLeft"
          arrow={false}
          overlayClassName="user-select-popover"
        >
          <Button
            className="add-user-btn"
            type="primary"
            shape="circle"
            size="small"
            icon={<PlusOutlined />}
          />
        </Popover>
      )}
    </Space>
  );
};

export default UserSelect;
